/**
 * ReviewModal — minimal modal for customers to submit a review.
 *
 * Props:
 *   open: bool
 *   onClose: () => void
 *   onSubmitted: (resp) => void
 *   booking: { booking_id, listing_id, listing_name, service_id, staff_id, ... }
 *   isRTL: bool
 *   apiClient: axios-like (must have .post)
 *
 * Network:
 *   POST /api/marketplace/reviews  (gated by marketplace.reviews.enabled)
 */
import React, { useState } from "react";
import { onBackdropMouseDown, onModalPanelMouseDown } from "../lib/modal";
import { Star, X, Send } from "lucide-react";
import { toast } from "sonner";

const StarRow = ({ value, onChange, label, testIdPrefix }) => (
  <div className="flex items-center justify-between gap-3 py-1.5">
    <span className="text-sm text-stone-700">{label}</span>
    <div className="flex gap-1" data-testid={`${testIdPrefix}-stars`}>
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          type="button"
          data-testid={`${testIdPrefix}-star-${n}`}
          onClick={() => onChange(n)}
          className="p-0.5 hover:scale-110 transition"
          aria-label={`${n} stars`}
        >
          <Star
            size={22}
            className={n <= (value || 0) ? "fill-amber-400 text-amber-400" : "text-stone-300"}
          />
        </button>
      ))}
    </div>
  </div>
);

export default function ReviewModal({ open, onClose, onSubmitted, booking, isRTL, apiClient }) {
  const [overall, setOverall] = useState(0);
  const [service, setService] = useState(0);
  const [staff, setStaff] = useState(0);
  const [cleanliness, setCleanliness] = useState(0);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);

  const reset = () => {
    setOverall(0); setService(0); setStaff(0); setCleanliness(0); setText(""); setBusy(false);
  };

  const close = () => { reset(); onClose && onClose(); };

  const submit = async () => {
    if (overall < 1) {
      toast.error(isRTL ? "اختر التقييم العام أولاً" : "Please rate overall first");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        booking_id: booking.booking_id,
        listing_id: booking.listing_id,
        rating_overall: overall,
        rating_service: service || null,
        rating_staff: staff || null,
        rating_cleanliness: cleanliness || null,
        text_ar: isRTL ? text : "",
        text_en: isRTL ? "" : text,
        service_id: booking.service_id || null,
        staff_id: booking.staff_id || null,
      };
      const r = await apiClient.post("/marketplace/reviews", payload);
      toast.success(isRTL ? "شكراً! مراجعتك بانتظار الموافقة." : "Thanks! Your review is pending approval.");
      onSubmitted && onSubmitted(r.data);
      close();
    } catch (e) {
      toast.error(e?.response?.data?.detail || (isRTL ? "تعذّر الإرسال" : "Submit failed"));
    } finally {
      setBusy(false);
    }
  };

  if (!open || !booking) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 p-0 sm:p-4" data-testid="review-modal" onMouseDown={(e) => onBackdropMouseDown(e, close)}>
      <div className="w-full sm:max-w-md bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl p-5 sm:p-6 max-h-[92vh] overflow-y-auto" onMouseDown={onModalPanelMouseDown}>
        <div className="flex items-start justify-between gap-3 mb-2">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-stone-500">{isRTL ? "تقييم تجربتك" : "Rate your visit"}</div>
            <h3 className="font-bold text-stone-800 text-lg leading-tight mt-0.5">
              {(booking.listing_name && (booking.listing_name.ar || booking.listing_name.en)) || (isRTL ? "الصالون" : "Salon")}
            </h3>
            {booking.service_name && (
              <div className="text-xs text-stone-500 mt-0.5">{booking.service_name} {booking.date ? `· ${booking.date}` : ""}</div>
            )}
          </div>
          <button onClick={close} data-testid="review-modal-close" className="p-1 rounded hover:bg-stone-100 text-stone-500">
            <X size={18} />
          </button>
        </div>

        <div className="mt-3 divide-y divide-stone-100">
          <StarRow value={overall} onChange={setOverall} label={isRTL ? "التقييم العام *" : "Overall *"} testIdPrefix="rev-overall" />
          <StarRow value={service} onChange={setService} label={isRTL ? "الخدمة" : "Service"} testIdPrefix="rev-service" />
          <StarRow value={staff} onChange={setStaff} label={isRTL ? "الموظف" : "Staff"} testIdPrefix="rev-staff" />
          <StarRow value={cleanliness} onChange={setCleanliness} label={isRTL ? "النظافة" : "Cleanliness"} testIdPrefix="rev-cleanliness" />
        </div>

        <div className="mt-4">
          <label className="block text-xs text-stone-500 mb-1">{isRTL ? "تعليقك (اختياري)" : "Comment (optional)"}</label>
          <textarea
            data-testid="review-text"
            value={text}
            onChange={(e) => setText(e.target.value.slice(0, 1000))}
            rows={3}
            placeholder={isRTL ? "شاركنا تجربتك..." : "Share your experience..."}
            className="w-full px-3 py-2 rounded-lg border border-stone-200 focus:border-[#7A1F3D] outline-none text-sm"
          />
          <div className="text-[10px] text-stone-400 text-end mt-0.5">{text.length}/1000</div>
        </div>

        <div className="mt-2 text-[11px] text-stone-500 leading-relaxed bg-amber-50 border border-amber-100 rounded-lg p-2.5">
          {isRTL
            ? "مراجعتك ستخضع للمراجعة قبل النشر، ولا يمكن تعديلها بعد الإرسال."
            : "Your review goes to moderation first. You cannot edit it after submission."}
        </div>

        <div className="mt-4 flex gap-2 justify-end">
          <button onClick={close} disabled={busy} className="px-4 py-2 rounded-lg border border-stone-200 text-sm" data-testid="review-cancel">
            {isRTL ? "إلغاء" : "Cancel"}
          </button>
          <button onClick={submit} disabled={busy} data-testid="review-submit" className="px-4 py-2 rounded-lg bg-gradient-to-r from-[#7A1F3D] to-[#9B2950] text-white text-sm inline-flex items-center gap-1.5 disabled:opacity-50">
            <Send size={14} /> {busy ? (isRTL ? "..." : "...") : (isRTL ? "إرسال" : "Submit")}
          </button>
        </div>
      </div>
    </div>
  );
}
